import { Contact } from "@/interfaces/contacts";
import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import { PaginatedResponse } from "@/interfaces/paginatedResponse";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { ref } from "vue";
import { ToastInterface, useToast } from "vue-toastification";

interface Props {
  immediate?: boolean;
  params?: Params;
  config?: Config;
}

interface Config {
  errorHandling: boolean;
}

interface Params {
  id: string | number;
  page?: number;
}

interface CreateParams {
  hotelId: string | number;
  name: string;
  jobTitle: string;
  contactTypeIds: number[];
}

const getHotelContacts = async (
  params: Params,
  errorHandling = true,
): Promise<PaginatedResponse<Contact>> => {
  const { data } = await axios.get<PaginatedResponse<Contact>>(
    `/api/internal/hotels/${params.id}/contacts?page=${params.page}`,
    {
      errorHandling,
    } as CustomAxiosRequestConfig,
  );

  return data;
};

const createHotelContact = async (
  contact: CreateParams,
  toast: ToastInterface,
  params: Params,
) => {
  try {
    const { data } = await axios.post<Contact>(
      `/api/internal/hotels/${params.id}/contacts`,
      contact,
    );
    toast.success("Se creo correctamente el contacto");

    return data;
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const useHotelContacts = (props: Props = {}) => {
  const toast = useToast();

  const { config } = props;
  const { errorHandling = true } = config || {};

  const page = ref<Number | String>(1);

  const { data, isLoading, isError, refetch } = useQuery(
    ["hotel-contacts", props.params],
    () =>
      getHotelContacts(
        {
          id: props.params?.id!,
          page: Number(page.value),
        },
        errorHandling,
      ),
    { enabled: props.immediate },
  );

  const createHotelContactMutation = useMutation((contact: CreateParams) =>
    createHotelContact(contact, toast, props.params!),
  );

  const setPage = (newPage: number) => {
    page.value = newPage;
  };

  return {
    hotelContacts: data,
    isLoading,
    isError,
    refetch,
    page,
    setPage,

    createHotelContact: createHotelContactMutation.mutate,
    createHotelContactAsync: createHotelContactMutation.mutateAsync,
    createHotelContactLoading: createHotelContactMutation.isLoading,
  };
};

export default useHotelContacts;
